import { useEffect, useRef, useState } from "react";
import offerService from "../services/offerService";
import { set } from "date-fns";
import { OfferItemModel } from "../models/OfferItemModel";
import Styles from "./ItemImageUploadComp.module.css";
import TruncateTextComp from "./TruncateTextComp";
import { formatPrice } from "../utils/currencyUtils";

interface ViewOfferItemCompProps {
    show: boolean;
    offerItemId: number;
    onClose: () => void;
}

const ViewOfferItemComp: React.FC<ViewOfferItemCompProps> = ({ show, offerItemId, onClose }) => {
    const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const [loading, setLoading] = useState(false);
    const [item, setItem] = useState<OfferItemModel>();
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });


    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            loadOfferItem(offerItemId);
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const loadOfferItem = async (offerItemId: number) => {
        setServiceStatus({ type: "", message: "" });
        setLoading(true);
        try {
            const fetchedItem = await offerService.getOfferItem(offerItemId);
            setItem(fetchedItem);
        } catch (err) {
            setServiceStatus({ type: "Error", message: "Failed to load offer item details. Please try again later." });

        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog modal-lg">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Offer Item #{item?.offerItemId}</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>
                    <div className="modal-body">
                        {loading && (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <div className="spinner-border text-dark" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                        {!loading && item && (<>
                            <h5 className=" mb-3">{item?.itemName}</h5>
                            <div className="d-flex mb-3"
                                style={{
                                    gap: "0.5rem", // replaces g-2 spacing
                                    overflowX: "auto",
                                    whiteSpace: "nowrap",
                                    maxWidth: "100%",
                                }}>
                                {item.itemImages.map((img, index) => (
                                    <div key={index}
                                        className=" position-relative me-1"
                                        style={{ width: "142px" }}
                                    >
                                        <div className={`img-container ${Styles.imgContainer} rounded-0 border`} >
                                            <img src={`${itemImageURL}/${img}`}
                                                alt={`img-${index}`}
                                                className={`img-thumbnail ${Styles.imgThumbnail} border-0`}
                                            />
                                        </div>

                                        {(img === item.primaryImage) && (
                                            <div className={`${Styles.primaryImageLabel}`}><i className="bi bi-stars"></i> Cover Image</div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className='row g-3 mb-3'>

                                <div className="col-md-12 mb-3">
                                    <label className="text-label">Description</label>
                                    <span className="text-value ">
                                        <TruncateTextComp text={item.description} showScrollbar={true}></TruncateTextComp>
                                    </span>
                                </div>
                                <div className="col-md-2">
                                    <label className="text-label">Price</label>
                                    <p className="text-value mb-2">{formatPrice(item.price ?? 0)}</p>
                                </div>
                            </div>
                            {
                                item.infoLinkTitle && item.infoLinkUrl && item.infoLinkText &&
                                <div className="row g-3 mb-3">
                                    <div className="col-md-8">
                                        <label className="text-label mt-1">{item?.infoLinkTitle}</label>
                                        <p className="text-muted"><i className="bi bi-link-45deg"></i> 
                                            <a href={item.infoLinkUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-muted underline">
                                                {item.infoLinkText}
                                            </a>
                                        </p>
                                    </div>

                                </div>
                            }

                            <div className="row g-3 mb-3" >
                                <div className="col-md-8">
                                    <label className="text-label mt-1">Available Regions</label>
                                    <p className="text-value mb-2 one-line-text-ellipsis">{item.availableRegion || "-"}</p>
                                </div>
                            </div>

                        </>)
                        }

                        <div className='row g-3'>
                            {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                        </div>
                    </div>
                    <div className="modal-footer d-flex ">
                        <button className="btn btn-outline-dark rounded-0 float-end" onClick={onClose}>
                            Close
                        </button>

                    </div>
                </div>
            </div >
        </div >
    );
};
export default ViewOfferItemComp;